import { parseUrlInput } from "../normalizeInput";
import { bearerAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import { exactUrlToRegex } from "../urlPatternConversion";
import logo from "./logo.svg";

function phoenixBaseUrl(instanceUrl: string | undefined): string {
  const url = parseUrlInput(instanceUrl ?? "");
  return url && exactUrlToRegex(url.href) ? url.href.replace(/\/+$/, "") : "";
}

export const arizePhoenix = defineConnector({
  identifier: "arizePhoenix",
  name: "Arize Phoenix",
  tagLine: "AI observability and evaluation with Phoenix",
  keywords: "Arize, Phoenix, LLM, traces, spans, datasets, prompts",
  logo,
  protocol: "http",
  tags: ["ai", "engineering", "observability"],
  defaultUrlPattern: ({ instanceUrl }) =>
    exactUrlToRegex(instanceUrl).replace(/\/\.\*$/, "/v1/.*"),
  auth: (app) => bearerAuth(app.secret.apiKey),
  steps: [
    {
      title: "Create a Phoenix API key",
      items: [
        {
          kind: "markdown",
          content:
            "Use a Phoenix deployment with authentication enabled and Phoenix 13.16.0 or later. This connector supports Phoenix, not Arize AX.\n\nIn Phoenix, open **Settings → API Keys** and create a user API key, or ask an administrator for a system API key for unattended automation. User keys inherit your role: **Viewer** is sufficient for read-only access; **Member** is needed for ordinary writes. Choose an expiration that suits your organization and save the key securely. See the [Phoenix API key guide](https://arize.com/docs/phoenix/settings/api-keys).",
        },
      ],
    },
    {
      title: "Enter your Phoenix details",
      items: [
        {
          kind: "baseUrl",
          label: "Phoenix instance URL",
          required: true,
          placeholder: "https://phoenix.example.com",
          helperText:
            "Your Phoenix HTTPS base URL, including any port and installation path, but without /v1 or an endpoint path. Credentials are attached only to this instance’s /v1/ API, not Arize AX or browser pages.",
          target: { kind: "configuration", field: "instanceUrl" },
        },
        {
          kind: "input",
          label: "Phoenix API key",
          required: true,
          helperText:
            "Enter a Phoenix user or system API key, not an Arize AX key. Replace this value when the key expires or is rotated.",
          target: { kind: "secrets", field: "apiKey" },
        },
      ],
    },
  ],
  connectionTest: {
    protocol: "http",
    request: ({ instanceUrl }) => {
      const baseUrl = phoenixBaseUrl(instanceUrl);
      return {
        url: baseUrl ? `${baseUrl}/v1/user` : "",
        method: "GET",
        expectedStatus: 200,
        expectedBodyContains: ['"id":', '"role":', '"auth_method":'],
      };
    },
  },
  publicEnvVars: ({ instanceUrl }): Record<string, string> => {
    const baseUrl = phoenixBaseUrl(instanceUrl);
    return baseUrl ? { PHOENIX_ENDPOINT: baseUrl } : {};
  },
  usageHint: () =>
    "Use the Phoenix v1 REST API under PHOENIX_ENDPOINT + /v1/. The connector supplies Authorization: Bearer automatically. Phoenix OTLP/HTTP trace ingestion uses /v1/traces; Arize AX and OTLP/gRPC are not supported by this connector.",
});
